import {
  DataProjectionCoordinate,
  getEulerInverse,
  SphericalPosition
} from '../projections';
import { Viewer } from '../main';
import { AssetType, PlanarConfig } from '../../../redux/assets-slice';
import slash from 'slash';
import {
  AmbientLight,
  BoxGeometry,
  Euler,
  LinearFilter,
  MathUtils,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  Texture,
  TextureLoader,
  Vector2,
  Vector3
} from 'three';
import { interpolate1D, planeFromVectors, roundDigit } from "../utilities";
import { PanoramicImageMaterial, PlanarImageMaterial } from "../rendering";
import { MouseControls } from "../controls";

import noImagePanoramicPath from "../../textures/misc/no-image-panoramic.png";
import noImagePlanarPath from "../../textures/misc/no-image-planar.png";
import { toast } from "../../../app";
import { SceneCameraState } from "../../../redux/camera-slice";
import { t } from "../../../localization";
import { ImageCache } from "./cache";
import { Bookmark } from '../../../redux/bookmarks-slice';

const imageCache = new ImageCache();
const loader = new TextureLoader();
const missingPanoramicTexture = loader.load(noImagePanoramicPath);
const missingPlanarTexture = loader.load(noImagePlanarPath);

export interface ImagePixel {
  u: number,
  v: number
}

export interface CameraLoadOptions {
  fov?: number
  angles?: number[]
  lookat?: DataProjectionCoordinate
  centerOnImage?: boolean
}

class ImagePosition {
  private _value;
  private image: CameraImage;

  constructor(image: CameraImage, position) {
    this.image = image;
    this._value = new Vector3(...position);
  }

  get offset() {
    let matrix = new Matrix4()
      .copy(this.image.matrix)
      .transpose();

    return new Vector3()
      .setFromMatrixPosition(matrix);
  }

  get height() {
    return this.image.height;
  }

  get uncorrectedEuler() {
    return this.image.rotation._euler;
  }

  get value() {
    let offset = new Vector3()
      .copy(this.offset)
      .applyEuler(this.image.rotation.eulerCSV);

    let position = new Vector3()
      .copy(this._value)
      .add(offset);

    return position;
  }

  get viewer() {
    return new DataProjectionCoordinate(this.value).toScene();
  }

  get marker() {
    const offset = new Vector3(0, 0, this.height);
    return this.viewer.sub(offset);
  }

  get aerial() {
    return new DataProjectionCoordinate(this.value).toAerial();
  }
}

class ImageRotation {
  public array = [0,0,0];
  public _euler: THREE.Euler;
  private image: CameraImage;

  constructor(image: CameraImage, rotation) {
    this.image = image;
    this.setRotation(rotation);
  }

  get matrix() {
    return new Matrix4()
      .copy(this.image.matrix)
      .setPosition(0,0,0);
  }

  get adjustment() {
    const angles = this.image.adjustment;
    return new Euler(
      -1 * MathUtils.degToRad(angles[0]),
      -1 * MathUtils.degToRad(angles[1]),
      -1 * MathUtils.degToRad(angles[2])
    );
  }

  get euler() {
    let rotationWithTransform = multiplyRotations(this._euler, this.matrix);
    let rotationWithAdjustment = multiplyRotations(rotationWithTransform, this.adjustment);

    let euler = new Euler()
      .setFromRotationMatrix(rotationWithAdjustment);

    return euler;
  }

  get viewer() {
    return new Euler().copy(this.euler);
  }

  get eulerCSV() {
    return this._euler;
  }

  get eulerDegrees() {
    let degrees = new Euler();
    degrees.x = -1 * MathUtils.radToDeg(this.euler.x);
    degrees.y = -1 * MathUtils.radToDeg(this.euler.y);
    degrees.z = -1 * MathUtils.radToDeg(this.euler.z);
    return degrees;
  }

  get eulerCsvInvert() {
    return getEulerInverse(this.eulerCSV);
  }

  setRotation(rotation) {
    this.array = rotation;
    this._euler = new Euler(
      -1 * MathUtils.degToRad(rotation[0]),
      -1 * MathUtils.degToRad(rotation[1]),
      -1 * MathUtils.degToRad(rotation[2])
    );
  }

  clear() {
    this.setRotation([0,0,0]);
  }
}

export class CameraImage {
  public id: string;
  public csv: string;
  public name: string;
  public path;
  public height;
  public config: PlanarConfig;
  public adjustment;
  public object;
  public enabled;
  public loaded = false;
  public loading = false;
  public missing = false;
  public ctx;
  public canvas;
  public materials;
  public geometry;
  public saved: boolean;
  public data;

  public image: HTMLImageElement;
  public imageWidth = 0;
  public imageHeight = 0;

  public scene: Scene;
  public camera: PerspectiveCamera;
  public matrix = new Matrix4();
  public controls: MouseControls;
  public position: ImagePosition;
  public rotation: ImageRotation;

  protected material: ShaderMaterial;
  protected missingImageTexture;

  constructor(data, controls, scene, camera) {
    this.id = data.id;
    this.csv = data.csv;
    this.name = data.name;
    this.path = data.path;
    this.height = data.height;
    this.adjustment = data.adjustment;
    this.config = data.config;
    this.saved = data.saved;

    this.data = data;
    this.scene = scene;
    this.camera = camera;
    this.controls = controls;

    this.position = new ImagePosition(this, data.position);
    this.rotation = new ImageRotation(this, data.rotation);

    this.reset();
  }

  get mimeType() {
    let extension = this.name.split(".").slice(-1)[0].toLowerCase();
    let imageTypes = {
      "jpg": "image/jpeg",
      "jpeg": "image/jpeg",
      "png": "image/png"
    };

    return imageTypes[extension] || null;
  }

  setCorrectionMatrix(matrix) {
    this.matrix = matrix;
  }

  pixelToDirection(pixel: ImagePixel) : Vector3 {
    console.warn("Not implemented");
    return new Vector3();
  }

  directionToPixel(direction: Vector3) : ImagePixel {
    console.warn("Not implemented");
    return null;
  }

  /** @private */
  setCameraPosition() {
    this.camera.position.copy(this.position.viewer);
    this.object.position.copy(this.position.viewer);
    this.object.rotation.copy(this.rotation.viewer);
  }

  /** @private */
  setCameraParameters(opts: CameraLoadOptions) {
    if (opts.fov) {
      this.controls.fov = opts.fov;
    }

    if (opts.lookat) {
      let position = new DataProjectionCoordinate(opts.lookat).toScene();
      this.controls.angles = positionToCameraAngle(
        this.object.position, position);
    } else if (opts.angles) {
      this.controls.angles = opts.angles;
    }
  }

  loadFromCache(bypassQueue=false) {
    return imageCache.get(this.path, this.mimeType, bypassQueue);
  }

  /** @private */
  async grabImageFromPath() {
    const {image, missing} = await this.loadFromCache(true);

    this.image = image;

    if (image) {
      this.imageWidth = image.width;
      this.imageHeight = image.height;
    }

    this.missing = missing;

    if (this.missing) {
      toast.error(t("toast.image-load-error", {
        name: this.name
      }));
    }
  }

  setObjectVisible(state) {
    this.object.visible = state;
  }

  activateCamera(opts: CameraLoadOptions = {}) {
    this.setCameraPosition();
    this.setCameraParameters(opts);
    this.loadImageData();
    this.setObjectVisible(true);

    this.loaded = true;
    this.loading = false;
  }

  async loadCameraData() {
    this.enabled = true;
    this.loaded = false;
    this.loading = true;

    this.generateObject();
    this.setObjectVisible(false);
    await this.grabImageFromPath();
  }

  applyTempRotation(angles) {
    const angleEuler = new Euler(
      -1 * MathUtils.degToRad(angles[0]),
      -1 * MathUtils.degToRad(angles[1]),
      -1 * MathUtils.degToRad(angles[2])
    );

    const tempRotation = multiplyRotations(
      this.rotation.eulerCSV, angleEuler);
    const euler = new Euler()
      .setFromRotationMatrix(tempRotation);

    if (this.object) {
      this.object.rotation.copy(euler);
    }

  }

  /** @private */
  generateObject() {
    const geometry = new BoxGeometry(10, 10, 10);
    const mesh = new Mesh(geometry, this.material);
    this.object = mesh;
    this.scene.add(this.object);
  }

  /** @private */
  loadImageData() {
    let imageTexture;

    if (this.missing) {
      imageTexture = this.missingImageTexture;
    } else {
      imageTexture = new Texture();
      imageTexture.image = this.image;
    }

    imageTexture.minFilter = LinearFilter;
    imageTexture.needsUpdate = true;

    this.object.material.setTexture(imageTexture);
  }

  destroy() {
    if (!this.enabled) return false;

    if (this.object) {
      this.scene.remove(this.object);
    }

    this.materials.forEach(material => {
      if (material.map) {
        material.map.dispose();
      }
      material.dispose();
    });

    if (this.geometry) {
      this.geometry.dispose();
    }

    this.image = null;
    this.reset();
  }

  reset() {
    this.object = null;
    this.enabled = false;
    this.materials = [];
    this.geometry = null;
  }
}

export class PanoramicImage extends CameraImage {
  protected missingImageTexture = missingPanoramicTexture

  constructor(data, mouseControls, scene, camera) {
    super(data, mouseControls, scene, camera);
    this.material = new PanoramicImageMaterial();
  }

  pixelToDirection(pixel: ImagePixel) : Vector3 {
    if (!pixel) return null;

    let width = this.imageWidth;
    let height = this.imageHeight;

    let theta = -1*MathUtils.degToRad(360 * (pixel.u / width) - 180);
    let phi = MathUtils.degToRad(180 * (pixel.v / height));

    let imageDirection = new Vector3(
      Math.cos(theta) * Math.sin(phi),
      Math.sin(theta) * Math.sin(phi),
      Math.cos(phi)
    );

    let direction = imageDirection.clone()
      .applyEuler(this.rotation.euler);

    return direction;
  }

  directionToPixel(direction: Vector3) : ImagePixel {
    if (!direction) return null;

    const inverse = getEulerInverse(this.rotation.euler);
    const imageDirection = direction.clone()
      .applyEuler(inverse);

    let sphere = new SphericalPosition()
      .fromVectors(new Vector3(0,0,0), imageDirection);

    let width = this.imageWidth;
    let height = this.imageHeight;

    let u = (width*(-sphere.theta + 180)/360);
    let v = (height*(sphere.phi)/180);

    u = (u + width) % width;
    v = (v + height) % height;

    return {u, v};
  }

  directionsToPlanar(directions, planarSize=100) {
    // Convert directons to a flat plane based on average normal
    const plane = planeFromVectors(directions);
    let planar = directions.map(x => {
      let target = new Vector3();
      plane.projectPoint(x, target);
      return target;
    });

    let allX = planar.map(x => x.x);
    let allY = planar.map(x => x.y);

    let xMin = Math.min(...allX);
    let xMax = Math.max(...allX);
    let yMin = Math.min(...allY);
    let yMax = Math.max(...allY);

    planar = planar.map(point => {
      point.x = planarSize * (point.x - xMin) / (xMax-xMin);
      point.y = planarSize * (point.y - yMin) / (yMax-yMin);
      point.z = 0.0;
      return point;
    });

    return planar;
  }

  interpolatePixels(points, width, height) {
    let interpolated = [];

    // Convert pixels to angles
    let theta = points.map(x => MathUtils.degToRad((360 * x[0] / width) - 180));
    let phi = points.map(x => MathUtils.degToRad(180 * x[1] / height));

    // Convert angles to xyz
    let xyz = [];
    for (var i=0;i<points.length;i++) {
      xyz.push([
        Math.cos(theta[i]) * Math.sin(phi[i]),
        Math.sin(theta[i]) * Math.sin(phi[i]),
        Math.cos(phi[i])
      ]);
    }

    // Interpolate data along each axis
    let xyzTemp = [];
    let stepSize = 0.02;
    xyz.forEach((xyz1, index) => {
      let nextIndex = (index+1) % xyz.length;
      let xyz2 = xyz[nextIndex];
      let distance = new Vector3(...xyz1)
        .distanceTo(new Vector3(...xyz2));

      if (distance === 0) {
        return;
      }

      let steps = Math.ceil(distance/stepSize) + 1;
      let _x = interpolate1D(xyz1[0], xyz2[0], steps);
      let _y = interpolate1D(xyz1[1], xyz2[1], steps);
      let _z = interpolate1D(xyz1[2], xyz2[2], steps);

      let interpTemp = [];
      for (var i=0;i<steps;i++) {
        interpTemp.push([_x[i], _y[i], _z[i]]);
      }

      xyzTemp = [...xyzTemp, ...interpTemp];
    });
    xyz = [...xyzTemp];

    // Remove duplicates
    let xyzNoDups = [];
    xyz.forEach((current, index) => {
      if (index === 0) {
        xyzNoDups.push(current);
        return;
      }

      let previous = xyz[index - 1];
      let distance = new Vector3(...current)
        .distanceTo(new Vector3(...previous));

      if (distance !== 0) {
        xyzNoDups.push(current);
      };
    });
    xyz = [...xyzNoDups];

    // Convert xyz back to pixels
    xyz.forEach(current => {
      let r = new Vector2(...current).length();
      let _theta = MathUtils.radToDeg(Math.atan2(current[1], current[0]));
      let _phi = MathUtils.radToDeg(Math.atan2(r, current[2]));

      interpolated.push([
        roundDigit(width * ((_theta + 180) / 360), 4),
        roundDigit(height * (_phi / 180), 4)
      ]);
    });

    return interpolated;
  };
}

export class PlanarImage extends CameraImage {
  protected missingImageTexture = missingPlanarTexture

  constructor(data, mouseControls, scene, camera) {
    super(data, mouseControls, scene, camera);
    this.material = new PlanarImageMaterial(this.config);
  }

  pixelToDirection(pixel: ImagePixel) : Vector3 {
    if (!pixel) return null;

    let x = -1 * (pixel.u - this.config.cx) / this.config.fx;
    let y = (pixel.v - this.config.cy) / this.config.fy;

    let xyz = new Vector3(x, y, 1.0)
      .normalize()
      .multiplyScalar(-1.0);

    let direction = xyz.clone()
      .applyEuler(this.rotation.euler);

    return direction;
  }

  directionToPixel(direction: Vector3, margin=50) : ImagePixel {
    if (!direction) return null;

    const inverse = getEulerInverse(this.rotation.euler);

    const imageDirection = direction.clone()
      .applyEuler(inverse);

    let xyz = new Vector3()
      .copy(imageDirection)
      .divideScalar(imageDirection.z);

    let u = -xyz.x * this.config.fx + this.config.cx;
    let v = xyz.y * this.config.fy + this.config.cy;

    let outOfBounds = (u < -margin)
      || (v < -margin)
      || (u > (this.config.width + margin))
      || (v > (this.config.height + margin));

    if (outOfBounds) return null;

    return {u, v};
  }
}

export class CameraList {
  public viewer: Viewer;
  public scene: Scene;
  public cameras: CameraImage[] = [];
  private mappingByID = {};
  private mappingByName = {};
  public matrix = new Matrix4();
  public aligned = false;
  public waitForCameras = false;
  public current: CameraImage | null = null;
  public newCameraLoading = false
  public cameraLoadedOnce = false;
  private cameraHeight;
  private cameraAdjustments;

  constructor(viewer) {
    this.viewer = viewer;

    this.initScene();
    this.initEvents();
  }

  get count() {
    return this.cameras.length;
  }

  get camera() {
    return this.viewer.camera;
  }

  get controls() {
    return this.viewer.controls;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get minimap() {
    return this.viewer.minimap;
  }

  get markers() {
    return this.viewer.markers;
  }

  get currentIndex() {
    return this.cameras.indexOf(this.current);
  }

  get cameraLoaded() {
    return this.current !== null;
  }

  get isEmpty() {
    return this.cameras.length === 0;
  }

  reconcile(camerasFiles) {
    const newCameras = new Set(camerasFiles.map(camera => camera.id));
    const currentCameras = new Set(this.cameras.map(camera => camera.id));
    const camerasToLoad = camerasFiles.filter(camera => !currentCameras.has(camera.id));
    const camerasToDelete = this.cameras.filter(camera => !newCameras.has(camera.id));

    const numCamerasRemoved = camerasToDelete.length;
    const numCamerasAdded = camerasToLoad.length;

    // Delete existing cameras
    this.deleteCameras(camerasToDelete);

    // Add all new cameras
    this.addCameras(camerasToLoad);

    const cameraList = this.cameras;
    const numCameras = cameraList.length;

    // Redraw cameras in scene and aerial view after changes
    if ((numCamerasRemoved > 0) || (numCamerasAdded > 0)) {
      this.minimap.addCameraMarkers(cameraList);
      this.markers.addCameraMarkers(cameraList);
      this.controls.measurements.refreshMeasurementList();
    }

    // Update arrow marker navigation
    const changed = ((numCamerasAdded > 0) && (numCamerasAdded < numCameras));
    if ((numCamerasRemoved > 0) || changed) {
      this.markers.updateArrowNavigation();
    }

    return {numCameras, numCamerasAdded, numCamerasRemoved};
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  initEvents() {
    return;
  }

  onKeyDown(event) {
    if (event.key === "ArrowUp") {
      this.incrementCamera(1);
    } else if (event.key === "ArrowDown") {
      this.incrementCamera(-1);
    }
  }

  setWaitForCameras(state) {
    this.waitForCameras = state;
  }

  /** @private */
  setNormalCamera() {
    let controls = this.controls;
    controls.setOrbitValues(false);
  }

  /** @private */
  setOrbitCamera() {
    let controls = this.controls;
    controls.setOrbitValues(true);
  }

  /** @private */
  setCurrentImage(cameraImage: CameraImage | null) {
    this.current = cameraImage;
  }

  addCameras(cameras) {
    if (cameras.length === 0) {
      return;
    }

    // Get a list of new cameras
    let addedIndex = 0;
    let newCameras = new Array(cameras.length);

    cameras.forEach(camera => {
      const rotation = [
        camera.roll,
        camera.pitch,
        camera.yaw
      ];

      const position = [
        camera.x,
        camera.y,
        camera.z
      ];

      const adjustment = camera.id in this.cameraAdjustments
        ? this.cameraAdjustments[camera.id]
        : [0, 0, 0];

      const cameraConfig = camera.config
        ? camera.config
        : {};

      const assetType = camera.assetType;

      const cameraInfo = {
        id: camera.id,
        csv: camera.csv,
        name: camera.name,
        path: slash(camera.path),
        rotation: rotation,
        position: position,
        height: this.cameraHeight,
        adjustment: adjustment,
        config: cameraConfig,
        saved: camera.saved
      };

      // Add new camera from info
      let newCamera = this.getNewCamera(cameraInfo, assetType);
      if (camera) {
        newCameras[addedIndex] = newCamera;
        addedIndex += 1;
      }
    });

    if (addedIndex === 0) {
      return;
    }

    this.cameras = [...this.cameras, ...newCameras.slice(0, addedIndex)];
    this.updateCameraMapping();
  }

  deleteCameras(cameras) {
    if (cameras.length === 0) {
      return;
    }

    cameras.forEach(camera => {
      camera.destroy();
    });

    // Update camera array
    const toRemove = new Set(cameras.map(x => x.id));
    const currentRemoved = this.current && toRemove.has(this.current.id);
    const remaining = this.cameras.filter(x => !toRemove.has(x.id));

    this.cameras = [...remaining];
    this.updateCameraMapping();

    // Update camera markers
    this.minimap.removeCameraMarkers(cameras);
    this.markers.removeCameraMarkers(cameras);

    if (this.cameras.length === 0 || currentRemoved) {
      this.setCurrentImage(null);
      this.cameraLoadedOnce = false;
    }
  }

  /** @private */
  getNewCamera(data, assetType) {
    let cameraID = data.id;
    if (this.getByID(cameraID)) {
      // Camera already exists
      return;
    }

    let CameraObjectClass;
    if (assetType === AssetType.Panoramic) {
      CameraObjectClass = PanoramicImage;
    } else if (assetType === AssetType.Planar) {
      CameraObjectClass = PlanarImage;
    } else {
      return; // Unknown camera type
    }

    let camera = new CameraObjectClass(
      data,
      this.controls.mouseControls,
      this.scene,
      this.controls.object,
      this.matrix
    );

    camera.setCorrectionMatrix(this.matrix);

    return camera;
  }

  getByID(cameraID) {
    return this.mappingByID[cameraID];
  }


  getByName(cameraName) {
    return this.mappingByName[cameraName];
  }

  getByCSV(assetID) {
    return this.cameras.filter(camera => camera.csv === assetID);
  }

  getRowsCSV(cameras: CameraImage[]) {
    let csvEncompassOut = [];
    csvEncompassOut.push(['Filename', 'X', 'Y', 'Z','Roll', 'Pitch', 'Yaw']);

    cameras.forEach(camera => {
      let position = camera.position.value;
      let rotation = camera.rotation.eulerDegrees;

      csvEncompassOut.push([
        camera.name,
        position.x, position.y, position.z,
        rotation.x, rotation.y, rotation.z
      ]);
    });

    return csvEncompassOut;
  }

  updateCameraMapping() {
    // Direct mapping is required because using this.cameras.find
    // inside getByID / getByName is too slow for large number of cameras
    this.mappingByID = {};
    this.mappingByName = {};

    this.cameras.forEach(camera => {
      this.mappingByID[camera.id] = camera;
      this.mappingByName[camera.name] = camera;
    });
  }

  async loadImage(cameraID, opts:CameraLoadOptions = {}) {
    if (this.newCameraLoading) {
      return;
    }

    let camera = this.getByID(cameraID);
    if (!camera) {
      return;
    }

    const {centerOnImage=true} = opts;

    if (camera.enabled) {
      // Reload existing camera
      console.log(`Reload camera: ${camera.name}`);
      this.setNormalCamera();
      camera.setCameraParameters(opts);
      this.minimap.setActiveCamera(camera.id, centerOnImage);
      return camera;
    }

    // Load new camera
    console.log(`Load camera: ${camera.name}`);

    this.newCameraLoading = true;

    if (!this.cameraLoaded) {
      this.setWaitForCameras(true);
    }

    // Load image data from path
    await camera.loadCameraData();
    this.setCurrentImage(camera);

    // Clear previous cameras and switch to normal camera
    this.clearImages(camera);
    this.setNormalCamera();
    this.minimap.setActiveCamera(camera.id, centerOnImage);

    // Set camera visible and apply texture
    camera.activateCamera(opts);

    this.setWaitForCameras(false);
    this.updateArrowNavigation();

    this.cameraLoadedOnce = true;
    this.newCameraLoading = false;

    this.preloadNearbyImages();

    return camera;
  }

  preloadNearbyImages() {
    const currentIndex = this.viewer.getCurrentCameraIndex();

    // Preload the next image
    const nextIndex = this.viewer.getNextCamera(currentIndex);
    const nextImage = this.cameras[nextIndex];
    nextImage.loadFromCache();

    // Preload the previous image
    const previousIndex = this.viewer.getPreviousCamera(currentIndex);
    const previousImage = this.cameras[previousIndex];
    previousImage.loadFromCache();
  }

  preloadBookmarks(bookmarks: Bookmark[]) {
    bookmarks.forEach(bookmark => {
      const cameraID = bookmark.sceneState.camera;
      const camera = this.getByID(cameraID);
      if (!camera) return;
      camera.loadFromCache();
    });
  }

  updateArrowNavigation() {
    this.viewer.markers.updateArrowNavigation();
  }

  clearImages(currentCamera: CameraImage) {
    /** Destroy all images except the current one */
    this.cameras.forEach(camera => {
      if (camera.id === currentCamera.id) {
        return;
      }
      camera.destroy();
    });
  }

  setMeshVisible(state) {
    this.cameras.forEach(camera => {
      if (state && !camera.enabled) {
        return;
      }

      if (camera.object) {
        camera.object.visible = state;
      }
    });
  }

  incrementCamera(increment) {
    if (!this.current) return;

    let cameraIndex = this.currentIndex;
    let nextIndex = cameraIndex + increment;
    nextIndex = (nextIndex + this.count) % this.count ;
    let nextCamera = this.cameras[nextIndex];
    this.viewer.loadImage(nextCamera.id);
  }

  setCameraHeights(height) {
    this.cameraHeight = height;
    this.cameras.forEach(camera => {
      camera.height = height;
    });
    this.markers.recalculateAllMarkers();
  }

  setCameraAdjustments(adjustments) {
    this.cameraAdjustments = adjustments;
    this.cameras.forEach(camera => {
      const adjustment = camera.id in adjustments
        ? adjustments[camera.id]
        : [0, 0, 0];
      camera.adjustment = adjustment;
    });
  }

  setCameraMatrix(elements) {
    const newMatrix = new Matrix4().fromArray(elements);
    if (!this.matrix.equals(newMatrix)) {
      this.matrix = newMatrix;
      this.applyMatrixToCameras();
      this.aligned = true;
    }
  }

  /** Reset to identity matrix */
  resetCameraMatrix() {
    this.matrix = new Matrix4();
    this.applyMatrixToCameras();
    this.aligned = false;
  }

  applyMatrixToCameras() {
    this.minimap.recalculateAllMarkers();
    this.markers.recalculateAllMarkers();

    this.cameras.forEach(camera => {
      camera.setCorrectionMatrix(this.matrix);
    });

    if (this.current) {
      this.setNormalCamera();
      this.current.setCameraPosition();
    }

    this.markers.updateArrowNavigation();
  }

  /** @private */
  loadSavedCameraState(savedCameraState: SceneCameraState) {
    let savedcameraID;
    const activeCamera = savedCameraState.camera;
    const savedLookat = savedCameraState.lookat;

    if (activeCamera) {
      // Load saved camera based on saved camera id
      const cameraExists = this.getByID(activeCamera);

      if (cameraExists) {
        savedcameraID = activeCamera;
      }
    } else if (savedLookat) {
      // Load closest camera based on saved position
      const lookat = new DataProjectionCoordinate(savedLookat);
      const closestCameraID = this.closestCameraToPosition(lookat);

      if (closestCameraID) {
        savedcameraID = closestCameraID;
      } else {
        toast.warning(t("toast.camera-error-xyz"));
      }
    }

    if (!savedcameraID) return false;

    // Convert from SceneCameraState to CameraLoadOptions
    const lookatVector = savedCameraState.lookat
      ? new DataProjectionCoordinate(savedCameraState.lookat)
      : null;

    const cameraLoadOptions = {
      fov: savedCameraState.fov,
      angles: savedCameraState.angles,
      lookat: lookatVector,
      centerOnImage: false
    } as CameraLoadOptions;

    this.viewer.loadImage(savedcameraID, cameraLoadOptions);

    return true;
  }

  closestCameraToPosition(position: DataProjectionCoordinate): number | undefined {
    let minDistance = 10;
    let maxDistance = 500;

    let bestDistance = maxDistance;
    let cameraID;

    for (const camera of this.cameras) {
      const distance = position.distanceTo(camera.position.value);
      if (distance < bestDistance && distance <= maxDistance && distance >= minDistance) {
        bestDistance = distance;
        cameraID = camera.id;
      }
    }

    return cameraID;
  }

  update() {
    return;
  }
}

const multiplyRotations = (rotationA, rotationB) => {
  let matrixA = rotationA instanceof Euler
    ? new Matrix4().makeRotationFromEuler(rotationA)
    : rotationA;

  let matrixB = rotationB instanceof Euler
    ? new Matrix4().makeRotationFromEuler(rotationB)
    : rotationB;

  return new Matrix4().multiplyMatrices(matrixA, matrixB);
};

export const directionToCameraAngle = (direction: Vector3) : [number, number] => {
  return positionToCameraAngle(new Vector3(), direction);
};

export const positionToCameraAngle = (cameraPos: Vector3, position: Vector3) : [number, number] => {
  const sphere = new SphericalPosition().fromVectors(cameraPos, position);
  const theta = MathUtils.degToRad(sphere.theta);
  const phi = MathUtils.degToRad(90 - sphere.phi);

  return [theta, phi] as [number, number];
};